import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Paper, Box, Modal, Button, Typography, TextField, CircularProgress, Chip, IconButton, Divider } from '@mui/material';
import { AuthUserContext } from '../../session';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../../utils/EnvironmentSwitcher'; // Import environment config
import { getFhirToken } from '../../../utils/fhirAuth';

// Event source colors
const EVENT_COLORS = {
  google: '#1976D2',
  fhir: '#4CAF50',
  mock: '#FF9800'
};

const SchedPage = () => {
  const { authUser } = useContext(AuthUserContext);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isWalletUser, setIsWalletUser] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start: null,
    end: null
  });
  const [activeView, setActiveView] = useState('dayGridMonth');
  const [calendarApi, setCalendarApi] = useState(null);
  const [environmentInfo, setEnvironmentInfo] = useState(null);

  // Check user type and fetch events on component mount
  useEffect(() => {
    checkUserType();
    fetchAllEvents();
    setEnvironmentInfo({
      environment: config.environment,
      fhirUrl: config.fhirUrl,
      apiUrl: config.apiUrl
    });
  }, [authUser]);

  // Determine if user is a wallet user
  const checkUserType = useCallback(() => {
    const isWallet = !!authUser?.walletAddress || 
                    JSON.parse(localStorage.getItem('userSession') || '{}')?.mieWalletExists;
    setIsWalletUser(isWallet);
  }, [authUser]);

  // Fetch all events from different sources
  const fetchAllEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const [googleEvents, fhirEvents] = await Promise.all([
        fetchGoogleEvents(),
        fetchFHIREvents()
      ]);

      const combinedEvents = [
        ...formatGoogleEvents(googleEvents),
        ...formatFHIREvents(fhirEvents)
      ];

      setEvents(isWalletUser ? generateMockEvents() : combinedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
      if (isWalletUser) {
        setEvents(generateMockEvents());
      }
    } finally {
      setIsLoading(false);
    }
  }, [isWalletUser, authUser]);

  // Fetch Google Calendar events
  const fetchGoogleEvents = useCallback(async () => {
    if (isWalletUser) return [];
    const accessToken = localStorage.getItem('googleAccessToken');
    if (!accessToken) return [];

    try {
      const response = await axios.get(
        'https://www.googleapis.com/calendar/v3/calendars/primary/events',
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            timeMin: new Date().toISOString(),
            maxResults: 100,
            singleEvents: true,
            orderBy: 'startTime'
          }
        }
      );
      return response.data.items || [];
    } catch (error) {
      console.error('Google Calendar error:', error);
      return [];
    }
  }, [isWalletUser]);

  // Fetch FHIR events
  const fetchFHIREvents = useCallback(async () => {
    const mieFToken = await getFhirToken(false);
    if (!mieFToken || !authUser?.uid) return [];

    try {
      // Use the FHIR URL from environment config
      const response = await axios.get(
        `${config.fhirUrl}/fhir/Schedule?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`,
        {
          headers: { Authorization: `Bearer ${mieFToken}` }
        }
      );
      return response.data.entry || [];
    } catch (error) {
      console.error('FHIR error:', error);
      return [];
    }
  }, [authUser]);

  // Generate mock events for wallet users
  const generateMockEvents = useCallback(() => {
    const events = [];
    const today = new Date();
    
    for (let i = 0; i < 5; i++) {
      const start = new Date(today);
      start.setDate(today.getDate() + Math.floor(Math.random() * 30));
      start.setHours(9 + Math.floor(Math.random() * 8));
      
      const end = new Date(start);
      end.setHours(start.getHours() + 1);

      events.push({
        id: `mock-${i}`,
        title: `Mock Event ${i + 1}`,
        description: `This is a mock event for demonstration purposes.`,
        start,
        end,
        source: 'mock',
        color: EVENT_COLORS.mock
      });
    }
    return events;
  }, []);

  // Format FHIR events for the calendar
  const formatFHIREvents = useCallback((fhirEvents) => {
    return fhirEvents.map(event => ({
      id: event.resource?.id,
      title: event.resource?.comment || event.resource?.text?.div?.replace(/<[^>]*>/g, '') || 'No Title',
      description: event.resource?.text?.div?.replace(/<[^>]*>/g, '') || '',
      start: event.resource?.planningHorizon?.start,
      end: event.resource?.planningHorizon?.end,
      source: 'fhir',
      color: EVENT_COLORS.fhir
    }));
  }, []);

  // Format Google events for the calendar
  const formatGoogleEvents = useCallback((googleEvents) => {
    return googleEvents.map(event => ({
      id: event.id,
      title: event.summary || 'No Title',
      description: event.description || '',
      start: event.start?.dateTime || event.start?.date,
      end: event.end?.dateTime || event.end?.date,
      location: event.location,
      source: 'google',
      color: EVENT_COLORS.google
    }));
  }, []);

  // Handle event click to show details
  const handleEventClick = useCallback((info) => {
    setSelectedEvent(info.event);
    setIsModalOpen(true);
  }, []);

  // Handle date selection to create new event
  const handleDateSelect = useCallback((selectInfo) => {
    setNewEvent({
      title: '',
      description: '',
      start: selectInfo.start,
      end: selectInfo.end
    });
    setIsCreateModalOpen(true);
  }, []);

  // Create a new FHIR event
  const createFHIREvent = useCallback(async (event) => {
    const mieFToken = await getFhirToken(false);
    if (!mieFToken || !authUser?.uid) throw new Error('Not authenticated');

    const fhirEvent = {
      resourceType: 'Schedule',
      text: { 
        status: 'generated', 
        div: `<div>${event.description || event.title}</div>`
      },
      identifier: [{
        system: 'https://fhir.mieros.cloud/fhir/ids',
        value: authUser.uid
      }],
      active: true,
      planningHorizon: {
        start: event.start.toISOString(),
        end: event.end.toISOString()
      },
      comment: event.title
    };

    // Use the FHIR URL from environment config
    const response = await axios.post(
      `${config.fhirUrl}/fhir/Schedule`,
      fhirEvent,
      {
        headers: {
          Authorization: `Bearer ${mieFToken}`,
          'Content-Type': 'application/fhir+json'
        }
      }
    );

    return response.data.id;
  }, [authUser]);

  // Handle form submission for new event
  const handleCreateEvent = useCallback(async () => {
    if (!newEvent.title) {
      toast.error('Event title is required');
      return;
    }

    try {
      setIsLoading(true);
      
      // Create the event in FHIR
      const eventId = await createFHIREvent(newEvent);
      
      // Add the new event to the calendar
      const createdEvent = {
        id: eventId,
        title: newEvent.title,
        description: newEvent.description,
        start: newEvent.start,
        end: newEvent.end,
        source: 'fhir',
        color: EVENT_COLORS.fhir
      };
      
      setEvents(prevEvents => [...prevEvents, createdEvent]);
      setIsCreateModalOpen(false);
      toast.success('Event created successfully');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setIsLoading(false);
    }
  }, [newEvent, createFHIREvent]);

  // Handle calendar view change
  const handleViewChange = useCallback((viewInfo) => {
    setActiveView(viewInfo.view.type);
  }, []);

  // Refresh events
  const handleRefresh = useCallback(() => {
    fetchAllEvents();
    toast.info('Refreshing calendar events...');
  }, [fetchAllEvents]);

  // Custom event rendering function
  const renderEventContent = useCallback((eventInfo) => {
    const source = eventInfo.event.extendedProps.source;
    const sourceIcon = source === 'fhir' ? '🏥' : source === 'google' ? '📅' : '📌';
    
    return (
      <div className="fc-event-main-content" style={{ 
        padding: '2px 4px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        <div style={{ 
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {sourceIcon} {eventInfo.event.title}
        </div>
        <div style={{ 
          fontSize: '0.8em',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}>
          {eventInfo.timeText}
        </div>
      </div>
    );
  }, []);

  // Calendar options
  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: activeView,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    events,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekends: true,
    select: handleDateSelect,
    eventClick: handleEventClick,
    eventContent: renderEventContent,
    height: 'auto',
    viewDidMount: handleViewChange,
    datesSet: (dateInfo) => {
      // You could fetch events for the new date range here
    }
  }), [events, activeView, handleDateSelect, handleEventClick, renderEventContent, handleViewChange]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Box>
          <Typography variant="h4" component="h1">
            Schedule Manager
          </Typography>
          {environmentInfo && config.environment !== 'production' && (
            <Typography variant="caption" color="text.secondary">
              Environment: {environmentInfo.environment} | API: {environmentInfo.apiUrl}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Refresh'}
          </Button>
          
          <Button 
            variant="outlined" 
            color="primary"
            onClick={() => {
              const now = new Date();
              setNewEvent({
                title: '',
                description: '',
                start: now,
                end: new Date(now.getTime() + 60 * 60 * 1000) // 1 hour later
              });
              setIsCreateModalOpen(true);
            }}
          >
            Create Event
          </Button>
        </Box>
      </Box>
      
      {/* Event source legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Chip 
          label="Google Calendar" 
          sx={{ backgroundColor: EVENT_COLORS.google, color: 'white' }}
        />
        <Chip 
          label="Health Events" 
          sx={{ backgroundColor: EVENT_COLORS.fhir, color: 'white' }}
        />
        {isWalletUser && (
          <Chip 
            label="Demo Events" 
            sx={{ backgroundColor: EVENT_COLORS.mock, color: 'white' }}
          />
        )}
      </Box>
      
      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Calendar */}
      <Paper sx={{ 
        p: 2, 
        borderRadius: 2, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <FullCalendar {...calendarOptions} />
      </Paper>

      {/* Event Detail Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="event-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 300,
          maxWidth: '90vw',
          width: 400
        }}>
          {selectedEvent && (
            <>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                pb: 2,
                borderBottom: '1px solid #eee'
              }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h2">
                    {selectedEvent.title}
                  </Typography>
                  <Chip 
                    label={selectedEvent.extendedProps.source === 'fhir' ? 'Health Event' : 
                          selectedEvent.extendedProps.source === 'google' ? 'Google Calendar' : 
                          'Demo Event'} 
                    size="small"
                    sx={{ 
                      backgroundColor: selectedEvent.backgroundColor,
                      color: 'white',
                      mt: 1
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Time
                </Typography>
                <Typography variant="body2">
                  Start: {selectedEvent.start.toLocaleString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
                <Typography variant="body2">
                  End: {selectedEvent.end.toLocaleString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
              
              {selectedEvent.extendedProps.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {selectedEvent.extendedProps.description}
                  </Typography>
                </Box>
              )}
              
              {selectedEvent.extendedProps.location && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {selectedEvent.extendedProps.location}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                  onClick={() => setIsModalOpen(false)}
                  variant="contained"
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Create Event Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        aria-labelledby="create-event-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 300,
          maxWidth: '90vw',
          width: 400
        }}>
          <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
            Create New Event
          </Typography>
          
          <TextField
            label="Event Title"
            fullWidth
            value={newEvent.title}
            onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
            margin="normal"
            required
          />
          
          <TextField
            label="Description"
            fullWidth
            value={newEvent.description}
            onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Start Time
            </Typography>
            <Typography variant="body2">
              {newEvent.start?.toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>
          
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              End Time
            </Typography>
            <Typography variant="body2">
              {newEvent.end?.toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button 
              onClick={() => setIsCreateModalOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEvent}
              variant="contained"
              color="primary"
              disabled={isLoading || !newEvent.title}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Event'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SchedPage;
